// src/pages/ProgressPage.jsx
import React, { useEffect, useState, useRef } from "react";
import AppLayout from "../components/layout/AppLayout";
import api from "../api/api";
import Spinner from "../components/common/Spinner";
import { Link } from "react-router-dom";
import { XMarkIcon, DocumentTextIcon } from "@heroicons/react/24/solid";

/* ============================================================
    🔵 MODAL DE CERTIFICADO (iframe + print do PDF)
============================================================ */
function CertificateModal({ url, onClose }) {
  const iframeRef = useRef(null);

  if (!url) return null;

  const handlePrint = () => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.focus();
      iframeRef.current.contentWindow.print();
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-5xl h-[90vh] rounded-2xl overflow-hidden shadow-2xl relative flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-slate-900 text-white">
          <span className="font-bold text-lg">Certificado de Conclusão</span>
          <button onClick={onClose} className="hover:text-red-400 transition">
            <XMarkIcon className="w-8 h-8" />
          </button>
        </div>

        {/* Barra de ações */}
        <div className="flex gap-4 px-6 py-3 bg-slate-100 border-b border-slate-200">
          <a href={url} download className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-xl font-bold transition shadow-sm">
            ⬇ Baixar PDF
          </a>
          <button onClick={handlePrint} className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-xl font-bold transition shadow-sm">
            🖨 Imprimir
          </button>
          <a href={url} target="_blank" rel="noreferrer" className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-xl transition">
            Abrir em nova guia
          </a>
        </div>

        {/* PDF */}
        <div className="flex-1 bg-slate-200">
          <iframe ref={iframeRef} src={url} title="Visualização do Certificado" className="w-full h-full bg-white" />
        </div>
      </div>
    </div>
  );
}

/* ============================================================
    🔵 CÍRCULO DE PROGRESSO
============================================================ */
function CircularProgress({ value }) {
  const stroke = 7;
  const r = 38;
  const c = 2 * Math.PI * r;
  const progress = ((100 - value) / 100) * c;

  return (
    <svg width="90" height="90">
      <circle cx="45" cy="45" r={r} stroke="#1e293b" strokeWidth={stroke} fill="none" />
      <circle
        cx="45"
        cy="45"
        r={r}
        stroke={value >= 95 ? "#10b981" : "#6366F1"}
        strokeWidth={stroke}
        fill="none"
        strokeDasharray={c}
        strokeDashoffset={progress}
        strokeLinecap="round"
        style={{ transition: "1s ease" }}
      />
      <text x="50%" y="52%" textAnchor="middle" fill="#fff" className="font-bold text-sm">
        {value}%
      </text>
    </svg>
  );
}

/* ============================================================
    📄 PÁGINA PRINCIPAL – DASHBOARD
============================================================ */
export default function ProgressPage() {
  const [progress, setProgress] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalPDF, setModalPDF] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    let mounted = true;
    const loadData = async () => {
      try {
        const [cRes, certRes] = await Promise.all([
          api.get("/courses/with-status"),
          api.get("/certificates")
        ]);

        if (!mounted) return;

        // 🔥 LÓGICA DE ARREDONDAMENTO (Régua de 95%)
        const normalized = (cRes.data.courses || []).map(course => {
          let p = Number(course.progress);
          // Se o progresso for maior que 95, forçamos 100% para evitar o erro de arredondamento das aulas
          if (p >= 95) p = 100;
          
          return {
            ...course,
            progress: p,
            certificates_enabled: Number(course.certificates_enabled),
          };
        });

        const existingCerts = certRes.data || [];
        setProgress(normalized);
        setCertificates(existingCerts);

        // AUTO-GERAÇÃO
        normalized.forEach(course => {
          const jaTemCert = existingCerts.some(x => x.course_id === course.id);
          if (course.progress === 100 && course.certificates_enabled === 1 && !jaTemCert) {
            gerarCertificado(course.id);
          }
        });

      } catch (err) {
        setErrorMessage("Falha ao carregar dados de progresso.");
        setShowError(true);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadData();
    return () => { mounted = false; };
  }, []);

  async function gerarCertificado(courseId) {
    if (isGenerating) return;
    setIsGenerating(true);
    try {
      const res = await api.post(`/certificates/generate/${courseId}`);
      if (res.data.url) {
        const newCert = {
          course_id: courseId,
          certificate_url: res.data.url,
          certificate_hash: res.data.hash,
          created_at: new Date().toISOString(),
        };
        setCertificates((prev) =>
          prev.some((x) => x.course_id === courseId) ? prev : [...prev, newCert]
        );
        setModalPDF(res.data.url);
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Falha ao gerar certificado.");
      setShowError(true);
    } finally {
      setIsGenerating(false);
    }
  }

  if (loading) return <AppLayout><div className="min-h-screen flex items-center justify-center bg-slate-950"><Spinner /></div></AppLayout>;

  const total = progress.length;
  const concluidos = progress.filter((c) => c.progress === 100).length;
  const andamento = progress.filter((c) => c.progress > 0 && c.progress < 100).length;
  const naoIniciados = progress.filter((c) => c.progress === 0).length;
  const media = Math.round(progress.reduce((a, b) => a + (b.progress || 0), 0) / (total || 1));

  return (
    <AppLayout>
      <div className="p-10 min-h-screen text-white bg-slate-950">
        <h1 className="text-4xl font-black mb-10 text-indigo-400">📊 Dashboard - Meu Progresso</h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-10">
          {[ 
            { label: "Cursos", value: total },
            { label: "Concluídos", value: concluidos },
            { label: "Em andamento", value: andamento },
            { label: "Não iniciados", value: naoIniciados },
          ].map((i, idx) => (
            <div key={idx} className="bg-white/5 p-5 rounded-2xl text-center border border-white/10 shadow-xl">
              <p className="text-3xl font-extrabold text-indigo-400">{i.value}</p>
              <p className="text-sm opacity-70 uppercase tracking-widest">{i.label}</p>
            </div>
          ))}
        </div>

        <div className="flex justify-center mb-12">
          <div className="bg-slate-900 p-6 rounded-full border border-indigo-500/20 shadow-2xl">
            <CircularProgress value={media} />
          </div>
        </div>

        <h2 className="text-2xl font-bold mb-6">Meus Cursos</h2>
        <div className="grid gap-7 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {progress.map((c) => {
            const cert = certificates.find((x) => x.course_id === c.id)?.certificate_url;
            return (
              <div key={c.id} className={`p-6 rounded-[32px] border transition transform hover:-translate-y-2 ${c.progress === 100 ? "bg-emerald-950/30 border-emerald-500/30" : "bg-slate-900 border-slate-800"}`}>
                <Link to={`/cursos/${c.id}`}>
                  <h3 className="font-bold text-lg mb-4 h-14 line-clamp-2">{c.title}</h3>
                  <div className="flex justify-center mb-6">
                    <CircularProgress value={c.progress} />
                  </div>
                </Link>

                {c.progress === 100 ? (
                  Number(c.certificates_enabled) === 1 ? (
                    cert ? (
                      <button onClick={() => setModalPDF(cert)} className="w-full flex justify-center items-center gap-2 py-4 rounded-2xl bg-emerald-500 hover:bg-emerald-400 font-black text-emerald-950 transition shadow-lg">
                        <DocumentTextIcon className="w-5 h-5" /> VER CERTIFICADO
                      </button>
                    ) : (
                      <button onClick={() => gerarCertificado(c.id)} disabled={isGenerating} className="w-full py-4 rounded-2xl bg-indigo-600 hover:bg-indigo-500 font-black transition animate-pulse">
                        {isGenerating ? "GERANDO..." : "🏆 GERAR CERTIFICADO"}
                      </button>
                    )
                  ) : (
                    <div className="w-full py-4 rounded-2xl bg-slate-800 text-slate-500 font-bold text-center text-xs">CERTIFICADO INDISPONÍVEL</div>
                  )
                ) : (
                  <Link to={`/cursos/${c.id}`} className="w-full block text-center py-4 rounded-2xl bg-slate-800 hover:bg-slate-700 font-bold transition">CONTINUAR AULA</Link>
                )}
              </div>
            );
          })}
        </div>

        <CertificateModal url={modalPDF} onClose={() => setModalPDF(null)} />

        {showError && (
          <div className="fixed bottom-10 right-10 bg-red-500 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-4">
            <span>{errorMessage}</span>
            <button onClick={() => setShowError(false)}><XMarkIcon className="w-5 h-5" /></button>
          </div>
        )}
      </div>
    </AppLayout>
  );
}